import React, { Suspense, lazy, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import FloatingBookButton from '../FloatingBookButton';
import FloatingWhatsApp from '../FloatingWhatsApp';

const ChatBot = lazy(() => import('../ChatBot'));
const BookingTicker = lazy(() => import('../BookingTicker'));
import { ANCHOR_TO_SLUG, langPath, parsePath } from '../../utils/siteNav';

// Scroll to top on page change; legacy anchors (/#contact) jump to the page that now holds the section.
const RouteEffects = () => {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const anchor = hash.replace('#', '');
    if (anchor) {
      const el = document.getElementById(anchor);
      if (el) { setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 150); return; }
      const slug = ANCHOR_TO_SLUG[anchor];
      if (slug && parsePath(pathname).slug !== slug) { navigate(langPath(parsePath(pathname).lang, slug), { replace: true }); return; }
    }
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [pathname, hash, navigate]);
  return null;
};

// Chat + live ticker are not needed for the first paint — mount them once the browser is idle (or on first interaction).
const useDeferredWidgets = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const go = () => setReady(true);
    const id = 'requestIdleCallback' in window ? window.requestIdleCallback(go, { timeout: 3500 }) : setTimeout(go, 2500);
    window.addEventListener('pointerdown', go, { once: true });
    window.addEventListener('scroll', go, { once: true, passive: true });
    return () => {
      ('cancelIdleCallback' in window ? window.cancelIdleCallback : clearTimeout)(id);
      window.removeEventListener('pointerdown', go);
      window.removeEventListener('scroll', go);
    };
  }, []);
  return ready;
};

export default function PublicLayout({ children }) {
  const widgets = useDeferredWidgets();
  return (
    <div className="min-h-screen bg-white">
      <RouteEffects />
      <Header />
      {children}
      <Footer />
      <FloatingBookButton />
      <FloatingWhatsApp />
      {widgets && (
        <Suspense fallback={null}>
          <ChatBot />
          <BookingTicker />
        </Suspense>
      )}
    </div>
  );
}
